import * as React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { DEFAULT_DATE_RANGE_PARAM, WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import moment from 'moment';

export interface StateFilters {
  today: boolean;
  tomorrow: boolean;
  anyTime: boolean;
  exactDate: moment.Moment | null;
}

export const PickupAppointmentScheduledFilter: React.FC = () => {
  const controlLabelClasses = {
    label: 'font-medium text-sm py-1',
    root: 'm-0 hover:bg-blue-200 rounded-sm',
  };
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS, true);

  const [filterStates, setFilterStates] = React.useState<StateFilters>({
    today: false,
    tomorrow: false,
    anyTime: false,
    exactDate: null,
  });

  React.useEffect(() => {
    const { pickupAppointmentAt, pickupAppointmentStatus } = searchParams;
    setFilterStates({
      today: pickupAppointmentAt.exactDate === 'today' || pickupAppointmentAt.startDate === 'today',
      tomorrow:
        pickupAppointmentAt.exactDate === 'tomorrow' || pickupAppointmentAt.endDate === 'tomorrow',
      anyTime:
        pickupAppointmentStatus.includes('scheduled') &&
        !pickupAppointmentAt.exactDate &&
        !pickupAppointmentAt.startDate &&
        !pickupAppointmentAt.endDate,
      exactDate:
        pickupAppointmentAt.exactDate &&
        !['today', 'tomorrow'].includes(pickupAppointmentAt.exactDate)
          ? moment(pickupAppointmentAt.exactDate)
          : null,
    });
  }, [searchParams]);

  const handleChange = (v: 'today' | 'tomorrow' | 'anyTime') => {
    const newFilterState = filterStates;
    let newSearchParams: typeof searchParams = {
      pickupAppointmentStatus: null,
      pickupAppointmentAt: DEFAULT_DATE_RANGE_PARAM,
    };

    newFilterState[v] = !newFilterState[v];

    if (v === 'anyTime' && newFilterState.anyTime) {
      newSearchParams = {
        pickupAppointmentAt: DEFAULT_DATE_RANGE_PARAM,
        pickupAppointmentStatus: ['scheduled'],
      };
    } else if (newFilterState.today && newFilterState.tomorrow) {
      newSearchParams = {
        pickupAppointmentAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          startDate: 'today',
          endDate: 'tomorrow',
        },
        pickupAppointmentStatus: ['scheduled'],
      };
    } else if (newFilterState.today) {
      newSearchParams = {
        pickupAppointmentAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          exactDate: 'today',
        },
        pickupAppointmentStatus: ['scheduled'],
      };
    } else if (newFilterState.tomorrow) {
      newSearchParams = {
        pickupAppointmentAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          exactDate: 'tomorrow',
        },
        pickupAppointmentStatus: ['scheduled'],
      };
    }
    setSearchParams(newSearchParams);
  };

  return (
    <FormGroup className="px-2">
      <FormControlLabel
        classes={controlLabelClasses}
        control={
          <Checkbox
            size="small"
            checked={filterStates.today}
            onChange={() => handleChange('today')}
          />
        }
        label="Today"
      />
      <FormControlLabel
        classes={controlLabelClasses}
        control={
          <Checkbox
            size="small"
            checked={filterStates.tomorrow}
            onChange={() => handleChange('tomorrow')}
          />
        }
        label="Tomorrow"
      />
      <FormControlLabel
        classes={controlLabelClasses}
        control={
          <Checkbox
            size="small"
            checked={filterStates.anyTime}
            onChange={() => handleChange('anyTime')}
          />
        }
        label="Any time"
      />
    </FormGroup>
  );
};
PickupAppointmentScheduledFilter.displayName =
  'TrackAndTrace.Controls.filters.PickupAppointmentScheduledFilter';
